// src/components/analysis/detail/StrategicFinanceAnalysisDetail.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Avatar,
  Alert,
  Table,        // Import Table components
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StarIcon from '@mui/icons-material/Star';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoIcon from '@mui/icons-material/Info';
import SummarizeIcon from '@mui/icons-material/Summarize'; // New Icon

// Helper function to format keys for display
const formatEvaluationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Custom list item component for better formatting
const EnhancedListItem = ({ children }) => (
  <ListItem
    sx={{
      padding: 0,
      paddingY: 0.5,
      marginLeft: 0
    }}
  >
    <ListItemIcon sx={{ minWidth: 28 }}>
      <CheckCircleOutlineIcon color="primary" fontSize="small" />
    </ListItemIcon>
    <ListItemText
      primary={
        <Typography variant="body2">
          {children}
        </Typography>
      }
    />
  </ListItem>
);

// Section header component
const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" color="primary.main" fontWeight="bold">
      {title}
    </Typography>
  </Box>
);

// Helper to render simple key-value pairs safely
const renderSimpleField = (label, value, icon) => {
  const displayValue = (value === null || value === undefined || value === '') ? 'Not specified' : String(value);
  if (displayValue === 'Not specified') return null; // Don't render if not specified

  return (
    <Box sx={{ mb: 1, display: 'flex', ml: 2 }}>
       {icon && React.cloneElement(icon, { fontSize: 'small', sx: { mr: 1, color: 'primary.main' } })}
      <Typography variant="body2" fontWeight="medium" sx={{ minWidth: '150px' }}>
        {label}:
      </Typography>
      <Typography variant="body2">
        {displayValue}
      </Typography>
    </Box>
  );
};


const StrategicFinanceAnalysisDetail = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Invalid strategic financial data format. Please check the analysis results.
        </Alert>
      </Box>
    );
  }

  // Safely extract data from the new simplified structure
  const fundingRequest = data.funding_request || {};
  const useOfFundsSummary = data.use_of_funds_summary || [];
  const keyFinancialProjections = data.key_financial_projections || {};
  const previousInvestorsSummary = data.previous_investors_summary || 'Not specified';
  const exitStrategyMention = data.exit_strategy_mention || 'Not specified';
  const evaluation = data.evaluation || {};

  return (
    <Box>
      <Grid container spacing={4}>
        {/* Funding Request & Use of Funds */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader
              icon={<MonetizationOnIcon fontSize="small" />}
              title="Funding & Use of Funds"
            />

            {/* Funding Request */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalanceIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                Funding Request:
              </Typography>
              {renderSimpleField("Amount Sought", fundingRequest.amount_sought)}
              {renderSimpleField("Timeline", fundingRequest.timeline)}
              {(fundingRequest.amount_sought === 'Not specified' && fundingRequest.timeline === 'Not specified') && (
                 <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>Details not specified.</Typography>
              )}
            </Box>

            {/* Use of Funds Summary */}
            {Array.isArray(useOfFundsSummary) && useOfFundsSummary.length > 0 && useOfFundsSummary[0] !== 'Not specified' && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Use of Funds Summary:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {useOfFundsSummary.map((item, index) => (
                      <EnhancedListItem key={index}>
                        {item}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
             {(!Array.isArray(useOfFundsSummary) || useOfFundsSummary.length === 0 || useOfFundsSummary[0] === 'Not specified') && (
                 <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>Use of funds not specified.</Typography>
              )}
          </Paper>
        </Grid>

        {/* Key Financial Projections */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader
              icon={<AssessmentIcon fontSize="small" />}
              title="Key Financial Projections"
            />
            {renderSimpleField("Projection Period", keyFinancialProjections.projection_period, <TrendingUpIcon />)}
            {renderSimpleField("Key Assumptions", keyFinancialProjections.key_assumptions, <InfoIcon />)}

            {/* Render the detailed projections table */}
            {keyFinancialProjections.projections_table && Array.isArray(keyFinancialProjections.projections_table) && keyFinancialProjections.projections_table.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                  <AssessmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Projections Table:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'grey.200' }}>
                  <Table size="small" aria-label="financial projections table">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell><Typography variant="subtitle2">Year</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">Total Revenue</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">ARR</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">EBITDA</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">FCF</Typography></TableCell>
                        <TableCell align="right"><Typography variant="subtitle2">Gross Profit Margin (%)</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {keyFinancialProjections.projections_table.map((row) => (
                        <TableRow key={row.year}>
                          <TableCell component="th" scope="row">
                            {row.year ?? 'N/A'}
                          </TableCell>
                          <TableCell align="right">{row.total_revenue ?? 'N/A'}</TableCell>
                          <TableCell align="right">{row.arr ?? 'N/A'}</TableCell>
                          <TableCell align="right">{row.ebitda ?? 'N/A'}</TableCell>
                          <TableCell align="right">{row.fcf ?? 'N/A'}</TableCell>
                          <TableCell align="right">{row.gross_profit_margin_percent ?? 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              // Show message if table is missing or empty, but period/assumptions might still exist
              (keyFinancialProjections.projection_period === 'Not specified' && keyFinancialProjections.key_assumptions === 'Not specified') && (
                 <Typography variant="body2" sx={{ ml: 2, mt: 2, fontStyle: 'italic' }}>Detailed projections table not specified.</Typography>
              )
            )}
          </Paper>
        </Grid>

        {/* Previous Investors & Exit Strategy */}
        <Grid item xs={12}>
           <Paper elevation={3} sx={{ p: 3 }}>
             <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <SummarizeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Previous Investors Summary:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {previousInvestorsSummary}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <FlightTakeoffIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Exit Strategy Mention:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {exitStrategyMention}
                    </Typography>
                </Grid>
             </Grid>
           </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1 }} />
        Strategic Evaluation (Key Points)
      </Typography>

      <Grid container spacing={3}>
        {Object.entries(evaluation)
          .filter(([key]) => key !== 'overall_strategic_finance_clarity') // Use new overall key
          .map(([key, value]) => (
            <Grid item xs={12} sm={6} lg={4} key={key}> {/* Adjust grid size if fewer items */}
              <Card
                variant="outlined"
                sx={{
                  height: '100%', // Ensure cards have same height
                  borderWidth: 1,
                  borderColor: 'primary.light',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 1
                  }
                }}
              >
                <CardHeader
                  title={formatEvaluationKey(key)}
                  titleTypographyProps={{ fontWeight: 'bold', variant: 'subtitle1' }}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  }}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating
                        value={parseFloat(value.score)}
                        precision={0.5}
                        readOnly
                        emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {value.score}/5
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2">
                    {value.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {evaluation.overall_strategic_finance_clarity && ( // Use new overall key
        <Box sx={{ mt: 3 }}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 1,
              borderLeft: 4,
              borderColor: 'primary.main'
            }}
          >
            <CardHeader
              title="Overall Finance Planning Clarity (Key Points)" // Updated title
              titleTypographyProps={{ fontWeight: 'bold', variant: 'h6', color: 'primary.main' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Rating
                    value={parseFloat(evaluation.overall_strategic_finance_clarity.score)} // Use new overall key
                    precision={0.5}
                    readOnly
                    sx={{ color: 'primary.main' }}
                    emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    {evaluation.overall_strategic_finance_clarity.score}/5
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography variant="body1">
                {evaluation.overall_strategic_finance_clarity.summary}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default StrategicFinanceAnalysisDetail;
